/**
 * SystemCommonCodeCreatePage Component
 * 공통코드 추가/수정 페이지
 */
import React, { useEffect, useRef } from 'react';
import Loading from '@/shared/components/feedback/Loading';
import { useCommonCodeCreate } from '../hooks/useCommonCodeCreate';
import GroupInfoSection from './GroupInfoSection';
import CommonCodeItemCard from './CommonCodeItemCard';
import { MAX_VALUE_FIELDS } from '../constants/commonCode.constants';
import '../styles/SystemCommonCodeCreatePage.css';

const SystemCommonCodeCreatePage: React.FC = () => {
    const pageHeaderRef = useRef<HTMLDivElement>(null);

    const {
        grpCode,
        formData,
        commonCodes,
        loading,
        isEditMode,
        handleGroupChange,
        handleCommonCodeChange,
        handleAddCommonCode,
        handleRemoveCommonCode,
        handleRemoveValueField,
        handleAddValueField,
        handleReset,
        handleSubmit,
        handleCancel,
    } = useCommonCodeCreate();

    useEffect(() => {
        window.scrollTo(0, 0);
        requestAnimationFrame(() => {
            window.scrollTo(0, 0);
            pageHeaderRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
    }, []);

    useEffect(() => {
        if (!grpCode || commonCodes.length === 0) return;

        const scrollToTop = () => {
            (document.activeElement as HTMLElement)?.blur?.();
            window.scrollTo(0, 0);
            pageHeaderRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
        };

        [0, 100, 200].forEach(delay => setTimeout(scrollToTop, delay));
    }, [grpCode, commonCodes.length]);

    const lastCode = commonCodes[commonCodes.length - 1];
    const canAddValueField = lastCode && lastCode.visibleValueFields < MAX_VALUE_FIELDS;

    return (
        <div className="common-code-create-page">
            <div className="page-header" ref={pageHeaderRef}>
                <button className="back-btn" type="button" onClick={handleCancel}>
                    <i className="fas fa-arrow-left"></i>
                    공통코드 관리로
                </button>
                <h1 className="page-title">
                    {isEditMode ? '공통코드 수정' : '공통코드 추가'}
                </h1>
            </div>

            {loading && (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <Loading message="그룹 데이터를 불러오는 중..." />
                </div>
            )}

            <form onSubmit={handleSubmit} className="common-code-form" noValidate autoComplete="off">
                <GroupInfoSection formData={formData} onChange={handleGroupChange} />

                <div className="form-section">
                    <div className="form-section-header">
                        <h2 className="form-section-title">공통코드 정보</h2>
                        <div className="section-header-actions">
                            {!isEditMode && (
                                <button type="button" className="btn btn-primary" onClick={handleAddCommonCode}>
                                    <i className="fas fa-plus"></i>
                                    공통코드 추가
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleAddValueField(lastCode.id)}
                                disabled={!canAddValueField}
                            >
                                <i className="fas fa-plus"></i>
                                코드값 추가
                            </button>
                            <button type="button" className="btn btn-reset" onClick={handleReset}>
                                초기화
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {isEditMode ? '수정' : '등록'}
                            </button>
                        </div>
                    </div>
                    <div className="form-section-body">
                        {commonCodes.map((code, index) => (
                            <CommonCodeItemCard
                                key={code.id}
                                code={code}
                                index={index}
                                canDelete={commonCodes.length > 1}
                                onChange={handleCommonCodeChange}
                                onRemove={handleRemoveCommonCode}
                                onRemoveValueField={handleRemoveValueField}
                            />
                        ))}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SystemCommonCodeCreatePage;
